import { DocumentData, DocumentSnapshot, doc, getDoc, getDocFromCache, getDocFromServer } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { getFirestoreDB } from "@/firebase";
import { IResearch, Study } from "@/models";

/**
Extracts relevant data from a Firestore document and returns it as an
IResearch object.
@param document - the Firestore document to extract data from
@returns an IResearch object containing the extracted data
@throws an error if the document is malformed
 */
function extractData(document: DocumentData): IResearch {
    if (!document?.nctId) {
        throw new Error("Malformed document: missing nctId, or document is falsy");
    }

    const aiTranslation = document.a || {};
    const originalData = document.o || {};
    const sponsorData = document.s || {};

    return {
        nctId: document.nctId,
        briefTitle: aiTranslation.title || "",
        summary: aiTranslation.summary || "",
        description: aiTranslation.description || "",

        locations: document._locationMetadata || [],
        enrollment: originalData.enrollmentCount || "",
        overallStatus: originalData.overallStatus || "",
        phase: originalData.phases || "",
        completionDate: originalData.completionDate || "",
        primaryCompletionDate: originalData.primaryCompletionDateEstimate || "",
        startDate: originalData.startDate || "",
        studyType: originalData.studyType || "",
        conditions: document.c?.meshes || [],
        centralContactName: originalData.centralContact?.centralContactName || [],
        centralContactPhone: originalData.centralContact?.centralContactPhone || [],
        centralContactEmail: originalData.centralContact?.centralContactEmail || [],
        age: originalData.eligibility.ageCat || [],
        sex: originalData.eligibility.sex || "",
        sponsorData: {
            isSponsored: sponsorData.isSponsored,
            inclusion: sponsorData.inclusionCriteria,
            compensation: sponsorData.compensation,
        },
    };
}

/**
 * Fetches a study from Firestore based on its NCT ID.
 *
 * @param nctID - the NCT ID of the study to fetch
 * @returns the study data, or throws an error if the study does not exist
 */
async function getStudy(nctID: string): Promise<Study> {
    const db = getFirestoreDB();
    if (!nctID) {
        throw new Error("NCT ID is required to fetch a study");
    }
    const docRef = doc(db, "trialFinderStudy", nctID);
    let docSnap: DocumentSnapshot;
    try {
        docSnap = await getDocFromCache(docRef);
        console.debug("Cache hit for ", nctID);
    } catch {
        // Cache miss
        console.debug("Cache miss for ", nctID);
        docSnap = await getDocFromServer(docRef);
    }
    if (docSnap.exists()) {
        const data = extractData(docSnap.data());
        return { id: nctID, data };
    }
    return { id: nctID, data: null };
}

function useStudy(nctID: string | undefined, initialStudy?: Study | null) {
    const [isLoading, setIsLoading] = useState(!initialStudy);
    const [study, setStudy] = useState<Study | undefined>(() => {
        if (initialStudy) {
            return initialStudy;
        }
        return nctID ? { id: nctID } : undefined;
    });
    const [error, setError] = useState();

    useEffect(() => {
        if (!nctID) {
            setStudy(undefined);
            return;
        }
        if (initialStudy && initialStudy.id === nctID) {
            setStudy(initialStudy);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        getStudy(nctID)
            .then((fetchedStudy) => setStudy(fetchedStudy))
            .catch((reason) => setError(reason))
            .then(() => setIsLoading(false));
    }, [nctID, initialStudy?.id]);
    return { isLoading, study, error };
}

type StudiesById = Record<string, Study>;

function chunk<T>(arr: T[], size: number) {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

async function getStudyCacheFirst(nctID: string): Promise<Study> {
    const db = getFirestoreDB();
    const docRef = doc(db, "trialFinderStudy", nctID);

    let docSnap: DocumentSnapshot;
    try {
        docSnap = await getDocFromCache(docRef);
        console.debug("Cache hit for", nctID);
    } catch {
        console.debug("Cache miss for", nctID);
        docSnap = await getDocFromServer(docRef);
    }

    if (docSnap.exists()) {
        const data = extractData(docSnap.data());
        return { id: nctID, data };
    }
    return { id: nctID, data: null };
}

/**
 * Fetch multiple studies by NCT IDs (preserves input order).
 * Returns both an ordered array and a byId map.
 */
export async function getStudies(nctIds: string[]) {
    const ids = (nctIds ?? []).map((s) => s?.trim()).filter(Boolean);

    // de-dupe but preserve first occurrence order
    const seen = new Set<string>();
    const uniqueIds = ids.filter((id) => (seen.has(id) ? false : (seen.add(id), true)));

    if (uniqueIds.length === 0) {
        return { studies: [] as Study[], byId: {} as StudiesById };
    }

    // Chunking avoids a huge burst of requests; tune if needed.
    const batches = chunk(uniqueIds, 50);

    const results: Study[] = [];
    for (const batch of batches) {
        const batchResults = await Promise.all(batch.map(getStudyCacheFirst));
        results.push(...batchResults);
    }

    const byId: StudiesById = {};
    for (const s of results) byId[s.id] = s;

    // Preserve original order (including duplicates) if you want:
    // const studies = ids.map((id) => byId[id] ?? { id, data: null });
    // But usually you want unique, in-order:
    const studies = uniqueIds.map((id) => byId[id]);

    return { studies, byId };
}

export function useStudies(nctIds: string[], initial?: { studies: Study[]; byId: StudiesById } | null) {
    const normalizedIds = useMemo(() => {
        const ids = (nctIds ?? []).map((s) => s?.trim()).filter(Boolean);
        // de-dupe, preserve order
        const seen = new Set<string>();
        return ids.filter((id) => (seen.has(id) ? false : (seen.add(id), true)));
    }, [nctIds]);

    // good dependency key when the list content matters
    const idsKey = useMemo(() => normalizedIds.join("|"), [normalizedIds]);

    const [isLoading, setIsLoading] = useState(!initial);
    const [studies, setStudies] = useState<Study[]>(initial?.studies ?? []);
    const [byId, setById] = useState<StudiesById>(initial?.byId ?? {});
    const [error, setError] = useState<unknown>(undefined);

    useEffect(() => {
        let cancelled = false;

        if (normalizedIds.length === 0) {
            setIsLoading(false);
            setStudies([]);
            setById({});
            setError(undefined);
            return;
        }

        setIsLoading(true);
        setError(undefined);

        getStudies(normalizedIds)
            .then((res) => {
                if (cancelled) return;
                setStudies(res.studies);
                setById(res.byId);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e);
            })
            .finally(() => {
                if (cancelled) return;
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]); // only re-run when the content of nctIds changes

    return { isLoading, studies, byId, error };
}

export { getStudy, useStudy };
